import React from 'react';
import { ComponentList } from '../models/Component';
import { Accordion } from './Accordion/Accordion';
import { Articles } from './Articles/Articles';
import { ArticleImage } from './ArticleImage/ArticleImage';
import { ArticlePullQuote } from './ArticlePullQuote/ArticlePullQuote';
import { articlesAndInsightsTransformer } from './Articles/articlesTransformer';
import { ArticleLinks } from './ArticleLinks/ArticleLinks';
import { ButtonList } from './ButtonList/ButtonList';
import { learnMoreToButtonList } from './ButtonList/buttonListTransformer';
import { Callout, CalloutInputs } from './Callout/Callout';
import { calloutTransformer } from './Callout/calloutTransformer';
import { CallToAction } from './CallToAction/CallToAction';
import { callToActionTransformer } from './CallToAction/callToActionTransformer';
import { articleCardTrioTransformer } from './CardTrio/articleCardTrioInputs';
import { CardTrio } from './CardTrio/CardTrio';
import { cewCardTrioHelper } from './CardTrio/cardTrioInputs';
import { CeoLetter } from './CeoLetter/CeoLetter';
import { ceoLetterTransformer } from './CeoLetter/ceoLetterTransformer';
import { CollapsibleTable } from './CollapsibleTable/CollapsibleTable';
import { collapsibleTableTransformer } from './CollapsibleTable/collapsibleTableTransformer';
import { CompanyBanner } from './CompanyBanner/CompanyBanner';
import { Disclaimer } from './Disclaimer/Disclaimer';
import { featuredArticleTransformer } from './FeaturedArticle/featuredArticleTransformer';
import { FeaturedArticle } from './FeaturedArticle/FeaturedArticle';
import {
    GenericHeaderAndContent,
    GenericHeaderAndContentInputs,
} from './GenericHeaderAndContent/GenericHeaderAndContent';
import { HeroCarousel } from './HeroCarousel/HeroCarousel';
import { heroCarouselTransformer } from './HeroCarousel/heroCarouselTransformer';
import { HeroImage } from './HeroImage/HeroImage';
import { heroImageTransformer } from './HeroImage/heroImageTransformer';
import { ImageList } from './ImageList/ImageList';
import { imageListTransformer } from './ImageList/imageListTransformer';
import { InfographicRow } from './Infographic/InfographicRow';
import { infographicsTransformer } from './Infographic/infographicsTransformer';
import { InlineCallout } from './InlineCallout/InlineCallout';
import { InvestorCard, IInvestorCard } from './InvestorCard/InvestorCard';
import { KeyTakeaways } from './KeyTakeaways/KeyTakeaways';
import { LatestNewsAndUpdates } from './LatestNewsAndUpdates/LatestNewsAndUpdates';
import { LeaderHighlight } from './LeaderHighlight/LeaderHighlight';
import { leaderHighlightTransformer } from './LeaderHighlight/leaderHighlightTransformer';
import { MediaContacts } from './MediaContacts/MediaContacts';
import { MediaReport } from './MediaReport/MediaReport';
import { mediaReportTransformer } from './MediaReport/mediaReportTransformer';
import { NoopComponent } from './NoopComponent/NoopComponent';
import { OverviewImage } from './OverviewImage/OverviewImage';
import { Partners } from './Partners/Partners';
import { ParagraphHeader } from './ParagraphHeader/ParagraphHeader';
import { partnersHelper } from './Partners/partnersTransformer';
import { PolicyMap } from './PolicyMap/PolicyMap';
import { PolicyOverviewBlock } from './PolicyOverviewBlock/PolicyOverviewBlock';
import { PullQuote } from './PullQuote/PullQuote';
import { pullQuoteTransformer } from './PullQuote/pullQuoteTransformer';
import { QuoteCard } from './QuoteCard/QuoteCard';
import { RHBlock } from './RHBlock/RHBlock';
import { RHVideo } from './RHBlock/RHVideo';
import { SectionText } from './SectionText/SectionText';
import { SectionImageCard } from './SectionImageCard/SectionImageCard';
import { SectionVideoCard } from './SectionVideoCard/SectionVideoCard';
import { SmallLeadershipGrid } from './SmallLeadershipGrid/SmallLeadershipGrid';
import { SpectrumScholars } from './SpectrumScholars/SpectrumScholars';
import {
    TitleAndText,
    TitleAndTextContentInputs,
} from './TitleAndText/TitleAndText';
import { Timeline } from './Timeline/Timeline';
import { VOBHeader } from './ValueOfBroadband/VOBHeader';
import { VOBTimeline } from './ValueOfBroadband/VOBTimeline';
import { VOBWhatMattersMost } from './ValueOfBroadband/VOBWhatMattersMost';
import { Video, VideoInputs } from './Video/Video';
import { videoTransformer } from './Video/videoTransformer';
import { accordionTransformer } from './Accordion/accordionTransformer';
import { timelineTransformer } from './Timeline/timelineTransformer';
import { getVideoId } from '../shared/getVideoId';
import { VideoCallout, VideoCalloutInputs } from './VideoCallout/VideoCallout';
import { VideoCardTrio } from './VideoCardTrio/VideoCardTrio';
import { WhiteSpace } from './WhiteSpace/WhiteSpace';
import { WebTable } from './WebTable/WebTable';
import { webTableTransformer } from './WebTable/webTableTransformer';
import YoutubeCard from './YoutubeCard/YoutubeCard';
import { youtubeCardTransformer } from './YoutubeCard/youtubeCardTransformer';

export const PARAGRAPH_TYPES: ComponentList = {
    paragraph__callout: {
        component: Callout,
        dataTransformer: calloutTransformer,
    },
    paragraph__carousel: {
        component: HeroCarousel,
        dataTransformer: heroCarouselTransformer,
    },
    paragraph__field_company_banner: {
        component: CompanyBanner,
    },
    paragraph__hero_block_with_background: {
        component: HeroImage,
        dataTransformer: heroImageTransformer,
    },
    paragraph__featuredmapcontent: {
        component: PolicyMap,
    },
    paragraph__latest_news_and_updates_policy: {
        component: LatestNewsAndUpdates,
    },
    paragraph__featuredblock3article: {
        component: FeaturedArticle,
        dataTransformer: featuredArticleTransformer,
    },
    paragraph__page_title: {
        component: GenericHeaderAndContent,
        dataTransformer: (node) => ({
            title: node.field_title,
            largeTitle: true,
            background: node.field_background === `true`,
        }),
    },
    paragraph__learn_more: {
        component: ButtonList,
        dataTransformer: learnMoreToButtonList,
    },
    paragraph__overviewblock5: {
        component: PolicyOverviewBlock,
    },
    paragraph___landing_page_title_and_overview: {
        component: GenericHeaderAndContent,
        dataTransformer: (node) => ({
            largeText: true,
            standalone: true,
            body: node.field_landingoverviewcontent.value,
        }),
    },
    paragraph__overviewblock1image: {
        component: OverviewImage,
        dataTransformer: (node) => ({
            alt: node.field_overviewimage.alt,
            image: node.relationships.field_overviewimage.localFile,
        }),
    },
    paragraph__overviewblock3: {
        component: SmallLeadershipGrid,
    },
    paragraph__overviewblock4: {
        component: GenericHeaderAndContent,
        dataTransformer: (node) => ({
            largeText: true,
            largeTitle: true,
            title: node.field_overviewblock4title,
            body: node.field_overviewblock4text.value,
        }),
    },
    paragraph__resourcehubblock1: {
        component: NoopComponent,
    },
    rh_block: {
        component: RHBlock,
    },
    paragraph__remote_video_reference: {
        component: RHVideo,
    },
    paragraph__card_trio: {
        component: CardTrio,
        dataTransformer: cewCardTrioHelper,
    },
    paragraph__video_card_trio: {
        component: VideoCardTrio,
        dataTransformer: cewCardTrioHelper,
    },
    paragraph__article_card_trio: {
        component: CardTrio,
        dataTransformer: articleCardTrioTransformer,
    },
    paragraph__video: {
        component: Video,
        dataTransformer: videoTransformer,
    },
    paragraph__image_list_component: {
        component: ImageList,
        dataTransformer: imageListTransformer,
    },
    paragraph__static_image_component: {
        component: GenericHeaderAndContent,
        dataTransformer: (node) => ({
            title: node.field_component_title,
            body: node.field_body?.value,
        }),
    },
    paragraph__accordion_text_component: {
        component: Accordion,
        dataTransformer: accordionTransformer,
    },
    paragraph__timeline: {
        component: Timeline,
        dataTransformer: timelineTransformer,
    },
    paragraph__collapsible_table: {
        component: CollapsibleTable,
        dataTransformer: collapsibleTableTransformer,
    },
    paragraph__image_only_carousel_scaling: {
        component: NoopComponent,
    },
    paragraph__main_image_carousel: {
        component: SpectrumScholars,
    },
    paragraph__partners: {
        component: Partners,
        dataTransformer: partnersHelper,
    },
    paragraph__automatic_listing: {
        component: MediaContacts,
    },
    paragraph__media_report: {
        component: MediaReport,
        dataTransformer: mediaReportTransformer,
    },
    paragraph__ceo_letter: {
        component: CeoLetter,
        dataTransformer: ceoLetterTransformer,
    },
    paragraph__pull_quote: {
        component: PullQuote,
        dataTransformer: pullQuoteTransformer,
    },
    paragraph__call_to_action_component: {
        component: CallToAction,
        dataTransformer: callToActionTransformer,
    },
    paragraph__tile_row_1_column: {
        component: InfographicRow,
        dataTransformer: infographicsTransformer,
    },
    paragraph__tile_row_2_columns: {
        component: InfographicRow,
        dataTransformer: infographicsTransformer,
    },
    paragraph__tile_row_3_columns: {
        component: InfographicRow,
        dataTransformer: infographicsTransformer,
    },
    paragraph__tile_row_custom: {
        component: InfographicRow,
        dataTransformer: infographicsTransformer,
    },
    paragraph__single_image: {
        component: GenericHeaderAndContent,
        dataTransformer: (node): GenericHeaderAndContentInputs => ({
            title: node.field_single_image_title,
            image: node.relationships?.field_single_image.localFile,
            imageAlt: node.field_single_image?.alt,
        }),
    },
    paragraph__vob_header: {
        component: VOBHeader,
    },
    paragraph__vob_timeline: {
        component: VOBTimeline,
    },
    paragraph__vob_what_matters_most: {
        component: VOBWhatMattersMost,
    },
    paragraph__disclaimer: {
        component: Disclaimer,
    },
    paragraph__text: {
        component: GenericHeaderAndContent,
        dataTransformer: (node): GenericHeaderAndContentInputs => ({
            body: node.field_text_content?.value,
        }),
    },
    paragraph__rich_text: {
        component: GenericHeaderAndContent,
        dataTransformer: (node): GenericHeaderAndContentInputs => ({
            body: node.field_text_content?.value,
        }),
    },
    paragraph__horizontal_line: {
        component: () => (
            <div className="kite-container">
                <hr />
            </div>
        ),
    },
    paragraph__content_block_2_background: {
        component: GenericHeaderAndContent,
        dataTransformer: (node): GenericHeaderAndContentInputs => ({
            body: node.field_body?.value,
            largeText: true,
            smallBackground: node.field_content_block_bgcolor,
            title: node.field_title,
            mediumLargeTitle: true,
            turnLinkIntoButton: true,
        }),
    },
    paragraph__resourcehubhalfvideo: {
        component: VideoCallout,
        dataTransformer: (node): VideoCalloutInputs => {
            const video = node.relationships?.field_video;

            let videoId = ``;

            if (video?.field_url_for_video) {
                videoId = getVideoId(video.field_url_for_video);
            }
            if (video?.field_media_oembed_video) {
                videoId = getVideoId(video.field_media_oembed_video);
            }

            return {
                bgColor: node.field_background_color,
                imagePosition: node.field_image_position,
                title: node.field_resourcehubblock5title,
                text: node.field_resourcehubblock5text?.value,
                video: videoId,
                name: video?.name,
            };
        },
    },
    paragraph__resourcehubrelatedcontent: {
        component: CardTrio,
        dataTransformer: articleCardTrioTransformer,
    },
    paragraph__resourcehubblock2: {
        component: Video,
        dataTransformer: (node): VideoInputs => ({
            ...videoTransformer(node),
            title: node.field_firstblocktitle,
        }),
    },
    paragraph__news_content_row_of_2: {
        component: CardTrio,
        dataTransformer: articleCardTrioTransformer,
    },
    paragraph__overviewblock1: {
        component: GenericHeaderAndContent,
        dataTransformer: (node): GenericHeaderAndContentInputs => ({
            title: node.field_overviewblock1title,
            body: node.field_overviewtextblock1?.value,
            largeText: true,
            skinny: true,
        }),
    },
    paragraph__resourceshubblock1: {
        component: GenericHeaderAndContent,
        dataTransformer: (node): GenericHeaderAndContentInputs => ({
            title: node.field_resourcehubblock1title,
            largeTitle: true,
            background: true,
        }),
    },
    paragraph__title_and_text: {
        component: TitleAndText,
        dataTransformer: (node): TitleAndTextContentInputs => ({
            title: node.field_title,
            body: node.field_body?.value,
        }),
    },
    paragraph__leadership_parent_articles: {
        component: Articles,
        dataTransformer: articlesAndInsightsTransformer,
    },
    paragraph__article_body: {
        component: TitleAndText,
        dataTransformer: (node): TitleAndTextContentInputs => ({
            title: node.field_title,
            body: node.field_body?.value,
            isArticle: true,
        }),
    },
    paragraph__image: {
        component: ArticleImage,
        dataTransformer: (node) => ({
            verticalImage:
                node.relationships?.field_image?.relationships?.verticalImage
                    ?.localFile,
            horizontalImage:
                node.relationships?.field_image?.relationships?.horizontalImage
                    ?.localFile,
            alt: node?.relationships?.field_image?.field_media_image?.alt,
            caption: node?.field_subtitle,
            isVertical: node?.field_dimension,
        }),
    },
    paragraph__pull_quote_article: {
        component: ArticlePullQuote,
        dataTransformer: (node) => ({
            author: node.field_subtitle,
            text: node.field_body?.value,
            image: node.relationships?.field_image?.relationships
                ?.field_media_image?.localFile,
            alt: node?.relationships?.field_image?.field_media_image?.alt,
        }),
    },
    paragraph__inline_callout: {
        component: InlineCallout,
        dataTransformer: (node) => ({
            header: node.field_header,
            link: node.field_article_link,
            image: node.relationships?.field_image?.relationships
                ?.field_media_image?.localFile,
            imageAlt: node.relationships?.field_image?.field_media_image?.alt,
            body: node.field_body?.value,
        }),
    },
    paragraph__investor_card: {
        component: InvestorCard,
        dataTransformer: (node) => ({
            title: node?.field_investor_title,
            bodyCopy: node?.field_body?.value,
            investorImage:
                node?.relationships?.field_image?.relationships
                    ?.field_media_image?.localFile,
            investorImageAlt:
                node?.relationships?.field_image?.field_media_image?.alt,
        }),
    },
    paragraph__article_links: {
        component: ArticleLinks,
        dataTransformer: (node) => ({
            downloadText: node.field_download_button_text,
            link: node.field_link,
            documentUrl:
                node.relationships?.field_media?.relationships
                    ?.field_media_document?.localFile?.publicURL,
            documentName:
                node.relationships?.field_media?.relationships
                    ?.field_media_document?.localFile?.name,
        }),
    },
    paragraph__leadership_promotion: {
        component: LeaderHighlight,
        dataTransformer: leaderHighlightTransformer,
    },
    paragraph__white_space: {
        component: WhiteSpace,
        dataTransformer: (node) => ({
            space: node.field_space,
        }),
    },
    paragraph__paragraph_header: {
        component: ParagraphHeader,
        dataTransformer: (node) => ({
            heading: node.field_heading,
            subheading: node.field_subtitle,
            number: node.field_numbering,
        }),
    },
    paragraph__web_table: {
        component: WebTable,
        dataTransformer: webTableTransformer,
    },
    paragraph__youtube_card: {
        component: YoutubeCard,
        dataTransformer: youtubeCardTransformer,
    },
    paragraph__key_takeaways: {
        component: KeyTakeaways,
        dataTransformer: (node) => ({
            title: node.field_header,
            takeaways: node.field_takeaways,
        }),
    },
    paragraph__section_text: {
        component: SectionText,
        dataTransformer: (node) => ({
            heading: node.field_header,
            body: node.field_text_content.value,
        }),
    },
    paragraph__section_about_text: {
        component: SectionText,
        dataTransformer: (node) => ({
            isAboutText: true,
            heading: node.field_header,
            body: node.field_text_content.value,
        }),
    },
    paragraph__quote_card: {
        component: QuoteCard,
        dataTransformer: (node) => ({
            quote: node.field_card_quote_text,
            attribution: node.field_quote_card_attribution,
        }),
    },
    paragraph__section_video_card: {
        component: SectionVideoCard,
        dataTransformer: (node) => ({
            description: node.field_video_body_text,
            eyebrow: node.field_eyebrow_text,
            link: {
                title: node.field_article_link?.title,
                uri: node.field_article_link?.uri,
            },
            mediaLibVideoURL:
                node.relationships?.field_video_select?.relationships
                    ?.field_media_video_file?.localFile?.publicURL,
            posterImg: {
                localFile: node.relationships?.field_card_image?.localFile,
                alt: node.field_card_image?.alt,
            },
            title: node.field_video_card_title,
            youtubeVideoURL:
                node.relationships?.field_video_select
                    ?.field_media_oembed_video,
        }),
    },
    paragraph__section_image: {
        component: SectionImageCard,
        dataTransformer: (node) => ({
            eyebrow: node.field_eyebrow_text,
            title: node.field_title_section,
            image: {
                src: node.relationships?.field_image?.relationships
                    ?.field_media_image?.localFile,
                alt: node.relationships?.field_image?.field_media_image?.alt,
            },
        }),
    },
};
